// LOGOMAN PRO — generic aesthetic logo scorer.
// Ported from need_eye_exam_aesthetic_scorer_v3 (HTML standalone, MIT-spirit).
// Combines:
//   • Birkhoff M = O/(1+C)            — Order over Complexity, 1933
//   • Wagemans symmetry triad         — mirror / rotational / translational
//   • Wu 2025 stability mediator      — symmetry → stability → quality
//   • Hue-circle harmony              — distance to harmonics 0/30/60/90/120/150/180°
//   • φ (golden ratio) alignment      — title/slogan size ratio
// Operates on a LogoState slice — works for any logo, not just eye-care.

import type { LogoState } from "../state/logoStore";
import { ICONS } from "../assets/icons";

const PHI = 1.618033988749895;

/** Per-icon symmetry hints. 1 = perfect vertical mirror; 0 = no symmetry. */
const ICON_VERTICAL_SYMMETRY: Record<string, number> = {
  circle: 1, square: 1, triangle: 1, diamond: 1, hex: 1, star: 1,
  ring: 1, shield: 1, drop: 1, heart: 1, flame: 1, spark: 1,
  bolt: 0.45, infinity: 1, eye: 1, globe: 1, tree: 1,
  leaf: 0.35, wave: 0.6, moon: 0.25,
};
/** Rough rotational-symmetry hints. */
const ICON_ROTATIONAL: Record<string, number> = {
  circle: 1, ring: 1, hex: 1, star: 1, diamond: 0.7, square: 0.7,
  spark: 0.9, infinity: 0.5, globe: 0.6,
  triangle: 0.4, drop: 0.2, heart: 0.1, leaf: 0.1, wave: 0.1, moon: 0.1, flame: 0.2, eye: 0.3,
  shield: 0.2, tree: 0.2, bolt: 0.1,
};

const PALINDROMES = new Set(["OO","AVA","WOW","EYE","NEE","BOB","ANNA","OTTO","DEED","NOON","NUN"]);

export interface ScoreInputs {
  state: Pick<LogoState,
    "title" | "slogan" | "iconId" | "qrText" |
    "titleColor" | "sloganColor" | "iconColor" | "background" |
    "layout" | "titleSize" | "sloganSize" | "iconScale" |
    "customIcons">;
}

export interface AestheticScore {
  /** Final composite 0..~1 (most logos land in 0.25..0.75 range). */
  score: number;
  /** 0..100 percentage for UI display. */
  scorePercent: number;
  /** Letter grade A+ … D for quick read. */
  grade: string;

  // Birkhoff components
  M_raw: number;       // Order/(1+Complexity)
  order: number;
  complexity: number;

  // Wu 2025 derivatives
  stability: number;
  memorability: number;
  predictedQuality: number;

  // Wagemans + harmony breakdown (each 0..1)
  breakdown: {
    mirrorV: number;
    mirrorH: number;
    rotational: number;
    translational: number;
    phiAlign: number;
    equilibrium: number;
    colorHarmony: number;
    legibility: number;
    elements: number;     // raw count, not 0..1
    pathDensity: number;
  };

  /** 1–3 actionable suggestions to nudge the score up. */
  feedback: string[];
}

export function scoreLogo({ state }: ScoreInputs): AestheticScore {
  // ----- Resolve the active icon (if any) -----
  let iconV = 0, iconR = 0, iconPathCount = 0;
  if (state.iconId === "__qr__") {
    iconV = 1; iconR = 0.7; iconPathCount = 6; // QR is highly symmetric, dense
  } else if (state.iconId && state.iconId.startsWith("custom_")) {
    const ci = state.customIcons?.find((x) => x.id === state.iconId);
    if (ci) { iconV = 0.6; iconR = 0.3; iconPathCount = ci.paths.length; }
  } else if (state.iconId) {
    const def = ICONS.find((i) => i.id === state.iconId);
    if (def) {
      iconV = ICON_VERTICAL_SYMMETRY[def.id] ?? 0.6;
      iconR = ICON_ROTATIONAL[def.id] ?? 0.4;
      iconPathCount = def.paths.length;
    }
  }
  const hasIcon = !!state.iconId;
  const hasTitle = !!state.title.trim();
  const hasSlogan = !!state.slogan.trim();

  // ----- Layout-driven equilibrium + mirror axis bias -----
  let mirrorV = 0, mirrorH = 0, rotational = 0, translational = 0, equilibrium = 1;
  switch (state.layout) {
    case "icon-top":
      mirrorV = 0.8 + 0.2 * iconV;
      mirrorH = 0.2;
      rotational = 0.2 * iconR;
      translational = 0.3;
      equilibrium = 1;
      break;
    case "icon-left":
      mirrorV = 0.35 + 0.15 * iconV;
      mirrorH = 0.5;
      rotational = 0.25 * iconR;
      translational = 0.45;
      equilibrium = 0.7;
      break;
    case "icon-right":
      mirrorV = 0.35 + 0.15 * iconV;
      mirrorH = 0.5;
      rotational = 0.25 * iconR;
      translational = 0.45;
      equilibrium = 0.7;
      break;
    case "text-only":
      mirrorV = 0.65 + (PALINDROMES.has(state.title.toUpperCase()) ? 0.35 : 0);
      mirrorH = 0.2;
      rotational = 0;
      translational = 0.4;
      equilibrium = 1;
      break;
    case "icon-only":
      mirrorV = 0.6 + 0.4 * iconV;
      mirrorH = 0.4 + 0.4 * iconV;
      rotational = iconR;
      translational = 0.1;
      equilibrium = 1;
      break;
  }

  // ----- φ alignment: title/slogan ratio close to PHI = good -----
  let phiAlign = 0.6;
  if (hasTitle && hasSlogan && state.sloganSize > 0) {
    const ratio = state.titleSize / state.sloganSize;
    phiAlign = 1 - Math.min(1, Math.abs(ratio / PHI - 1));
    phiAlign = 0.4 + 0.6 * phiAlign;
  }

  // ----- Color harmony — average pairwise harmony of meaningful color pairs -----
  const pairs: Array<[string, string]> = [];
  if (hasIcon) pairs.push([state.iconColor, state.background]);
  if (hasTitle) pairs.push([state.titleColor, state.background]);
  if (hasIcon && hasTitle) pairs.push([state.titleColor, state.iconColor]);
  if (hasTitle && hasSlogan) pairs.push([state.titleColor, state.sloganColor]);
  const colorHarmony = pairs.length === 0
    ? 1
    : pairs.reduce((s, [a, b]) => s + pairHarmony(a, b), 0) / pairs.length;

  // ----- Element count + path density -----
  let elements = 0;
  if (hasIcon) elements += 1;
  if (hasTitle) elements += 1;
  if (hasSlogan) elements += 1;
  if (state.background !== "#ffffff" && state.background !== "#fff") elements += 0.5;
  if (state.iconScale > 1.5) elements += 0.5;
  const pathDensity = 0.05 * iconPathCount + (hasTitle ? 0.1 : 0) + (hasSlogan ? 0.1 : 0);

  // ===== Birkhoff Order / Complexity =====
  const O = (
    mirrorV * 1.6 +
    mirrorH * 0.4 +
    rotational * 1.0 +
    translational * 0.6 +
    phiAlign * 0.7 +
    colorHarmony * 0.9 +
    equilibrium * 0.7
  ) / 5.9;
  const C = (elements * 0.12 + pathDensity * 0.3) / 1.5;
  const M_raw = O / (1 + C);

  // ===== Wu 2025 stability + memorability =====
  const legibility = elements <= 4 ? 1 : Math.max(0, 1 - (elements - 4) * 0.15);
  const distinctColors = new Set([state.titleColor, state.sloganColor, state.iconColor, state.background]).size;
  const restraint = Math.max(0.4, 1 - (distinctColors - 2) * 0.18); // 2 colors = 1.0, 4 = 0.64
  const memorability = legibility * 0.55 + restraint * 0.45;

  const complexityNorm = Math.min(1, C);
  const stability = mirrorV * 0.5 + equilibrium * 0.3 + (1 - complexityNorm) * 0.2;
  const lowCogLoadFactor = legibility;
  const predictedQuality = stability * lowCogLoadFactor;

  // ===== Final composite score =====
  const score = M_raw * (0.5 + 0.5 * memorability) * (0.7 + 0.3 * predictedQuality);
  const scorePercent = Math.round(Math.min(1, score) * 100);
  const grade = toGrade(scorePercent);

  // ===== Feedback (best-bang-for-buck nudges) =====
  const feedback: string[] = [];
  if (colorHarmony < 0.55) feedback.push(`Color harmony is low (${(colorHarmony * 100).toFixed(0)}). Try a palette where colors share or complement on the hue wheel.`);
  if (mirrorV < 0.5) feedback.push(`Vertical symmetry is weak. ${state.layout === "icon-left" || state.layout === "icon-right" ? "An icon-top or text-only layout reads more balanced." : "Pick a more vertically symmetric icon."}`);
  if (legibility < 0.7) feedback.push(`Lots of competing elements. Hide tagline, simplify icon, or use a plain background to boost legibility.`);
  if (phiAlign < 0.5 && hasSlogan) feedback.push(`Title/tagline size ratio is awkward. Aim for roughly 1.6× — try title ${Math.round(state.sloganSize * PHI)}px or tagline ${Math.round(state.titleSize / PHI)}px.`);
  if (distinctColors > 3) feedback.push(`${distinctColors} distinct colors — restrict to 2-3 for stronger memorability.`);
  if (feedback.length === 0) feedback.push(`Strong design across all axes. Try variants on the Image tab to see if any score higher.`);

  return {
    score,
    scorePercent,
    grade,
    M_raw,
    order: O,
    complexity: C,
    stability,
    memorability,
    predictedQuality,
    breakdown: {
      mirrorV, mirrorH, rotational, translational, phiAlign,
      equilibrium, colorHarmony, legibility,
      elements, pathDensity,
    },
    feedback: feedback.slice(0, 3),
  };
}

// ---------- color helpers (HSL hue + saturation from hex) ----------
function hexToHue(hex: string): number {
  const c = parseHex(hex);
  if (!c) return 0;
  const { r, g, b } = c;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = h * 60;
  if (h < 0) h += 360;
  return h;
}
function hexToSat(hex: string): number {
  const c = parseHex(hex);
  if (!c) return 0;
  const max = Math.max(c.r, c.g, c.b);
  const min = Math.min(c.r, c.g, c.b);
  return max === 0 ? 0 : (max - min) / max;
}
function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const s = hex.replace("#", "");
  if (s.length !== 6) return null;
  return {
    r: parseInt(s.slice(0, 2), 16) / 255,
    g: parseInt(s.slice(2, 4), 16) / 255,
    b: parseInt(s.slice(4, 6), 16) / 255,
  };
}
function pairHarmony(hex1: string, hex2: string): number {
  const s1 = hexToSat(hex1), s2 = hexToSat(hex2);
  if (s1 < 0.1 || s2 < 0.1) return 1; // grayscale harmonizes with anything
  const h1 = hexToHue(hex1), h2 = hexToHue(hex2);
  let diff = Math.abs(h1 - h2) % 360;
  if (diff > 180) diff = 360 - diff;
  const harmonics = [0, 30, 60, 90, 120, 150, 180];
  const closest = harmonics.reduce((best, h) => Math.abs(h - diff) < Math.abs(best - diff) ? h : best, 999);
  return 1 - Math.min(1, Math.abs(closest - diff) / 30);
}
function toGrade(percent: number): string {
  if (percent >= 85) return "A+";
  if (percent >= 75) return "A";
  if (percent >= 65) return "B+";
  if (percent >= 55) return "B";
  if (percent >= 45) return "C+";
  if (percent >= 35) return "C";
  return "D";
}
